import {
  createFileRoute,
  type MakeRouteMatchUnion,
} from "@tanstack/react-router";
import { useContext, useMemo } from "react";
import * as dateFns from "date-fns";
import {
  AppError,
  EmptyState,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@bfc-software/pallet";
import { TFunction } from "i18next";
import { AuthContext, AuthUtils } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/webhooks-api-client";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/webhooks/endpoints/$endpoint/deliveries",
)({
  staticData: {
    crumb: (match: MakeRouteMatchUnion, t: TFunction) => {
      const params = match.params as {
        lang: string;
        org: string;
        location: string;
        endpoint: string;
      };
      const base = {
        lang: params.lang,
        org: params.org,
        location: params.location,
      };
      return [
        {
          label: t("page.webhookEndpoints.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints",
          params: base,
        },
        {
          label: t("page.webhookEndpoints.deliveries.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints/$endpoint/deliveries",
          params: { ...base, endpoint: params.endpoint },
        },
      ];
    },
  },
  component: RouteComponent,
});

function RelativeTimestamp({ value }: { value: string }) {
  const parsed = dateFns.parseISO(value);
  if (Number.isNaN(parsed.getTime())) {
    return <>{value}</>;
  }
  const relative = dateFns.formatDistanceToNowStrict(parsed, {
    addSuffix: true,
  });
  const absolute = dateFns.format(parsed, "PPpp");
  return (
    <Tooltip content={absolute} placement="top" whiteSpace="nowrap">
      <time dateTime={value}>{relative}</time>
    </Tooltip>
  );
}

export function RouteComponent() {
  const { lang, org, location, endpoint } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useMemo(() => apiClient(), []);

  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const warehouseId = AuthUtils.getWarehouseId(authState, orgId, location);
  const enabled = typeof orgId === "string" && typeof warehouseId === "string";

  const query = client.useQuery(
    "get",
    "/{orgId}/warehouses/{warehouseId}/deliveries",
    {
      params: {
        path: { orgId: orgId!, warehouseId: warehouseId! },
        query: { endpoint_id: endpoint },
      },
    },
    { enabled },
  );

  const deliveries = query.data?.data ?? [];

  return (
    <div className={palletOverrides.basicPageContent}>
      <div className={palletOverrides.basicPageActionArea}>
        <div className={palletOverrides.basicPageActionAreaLeft}>
          <h1>{t("page.webhookEndpoints.deliveries.title")}</h1>
        </div>
      </div>
      {query.isLoading || !enabled ? (
        <Loader type="page" />
      ) : query.isError ? (
        <AppError
          description={t("page.webhookEndpoints.deliveries.failed")}
          resolution={t("page.webhookEndpoints.deliveries.failureSupport")}
        />
      ) : (
        <Table
          aria-label={t("page.webhookEndpoints.deliveries.table.ariaLabel")}
          className={
            deliveries.length < 1 ? palletOverrides.fullHeightTable : ""
          }
          isResizable={false}
        >
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("page.webhookEndpoints.deliveries.table.eventType")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.webhookEndpoints.deliveries.table.status")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.webhookEndpoints.deliveries.table.httpStatus")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.webhookEndpoints.deliveries.table.attempts")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.webhookEndpoints.deliveries.table.attemptedAt")}
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {deliveries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <EmptyState
                    heading={t("page.webhookEndpoints.deliveries.empty")}
                  />
                </TableCell>
              </TableRow>
            ) : (
              deliveries.map((delivery) => (
                <TableRow key={delivery.id} data-test-role="delivery-row">
                  <TableCell>
                    <span data-test-role="delivery-event-type">
                      {delivery.event_type}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-test-role="delivery-status">
                      {delivery.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-test-role="delivery-http-status">
                      {delivery.http_status ?? "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-test-role="delivery-attempt-count">
                      {delivery.attempt_count}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span data-test-role="delivery-attempted-at">
                      <RelativeTimestamp value={delivery.attempted_at} />
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
